import { useEffect, useRef } from "react";
import { io, Socket } from "socket.io-client";

import { PARSED_ENV } from "@/constants/common";
import { authSelector } from "@/pages/Public/redux/auth.slice";
import { useAppSelector } from "@/redux/hooks";

export const MEDIA_JOBS_NAMESPACE = "/media-jobs";

export enum MediaSocketEvent {
	JOIN_SCENARIO = "joinScenario",
	LEAVE_SCENARIO = "leaveScenario",
	JOIN_WIZARD = "joinWizard",
	LEAVE_WIZARD = "leaveWizard",
	JOB_UPDATED = "job.updated",
	JOB_SUCCEEDED = "job.succeeded",
	JOB_FAILED = "job.failed",
	WIZARD_UPDATED = "wizard.updated",
}

export interface MediaJobEvent {
	id: number;
	type: string;
	status?: string;
	progress?: number;
	courseId?: number;
	wizardSessionId?: number;
	storyboardSceneId?: number;
	actualCostUsd?: number;
	errorCode?: string;
	errorMessage?: string;
	assets?: Array<{
		id: number;
		variantIndex: number;
		formatPresetId?: number | null;
		fullFilePath: string;
		width?: number;
		height?: number;
	}>;
}

interface UseMediaJobSocketOptions {
	courseId?: number;
	wizardSessionId?: number;
	onJobUpdated?: (event: MediaJobEvent) => void;
	onJobSucceeded?: (event: MediaJobEvent) => void;
	onJobFailed?: (event: MediaJobEvent) => void;
	onWizardUpdated?: (aggregate: unknown) => void;
}

/**
 * Live job progress for the editor and the wizard.
 *
 * Deliberately a separate connection from `SocketContext`, which is scoped to a running
 * exercise. Sockets here are an accelerator only — every consumer must still work if no
 * event ever arrives, because delivery is backed up by refetch-on-focus on the client and
 * a reconciliation cron on the server.
 */
export const useMediaJobSocket = ({
	courseId,
	wizardSessionId,
	onJobUpdated,
	onJobSucceeded,
	onJobFailed,
	onWizardUpdated,
}: UseMediaJobSocketOptions) => {
	const { token } = useAppSelector(authSelector);
	const socketRef = useRef<Socket | null>(null);

	// Held in a ref so a re-created callback does not tear the socket down and reconnect.
	// Written after commit, never during render: a ref mutated in the render body is not
	// safe under concurrent rendering, where a render can be thrown away.
	const handlers = useRef({ onJobUpdated, onJobSucceeded, onJobFailed, onWizardUpdated });

	useEffect(() => {
		handlers.current = { onJobUpdated, onJobSucceeded, onJobFailed, onWizardUpdated };
	}, [onJobUpdated, onJobSucceeded, onJobFailed, onWizardUpdated]);

	useEffect(() => {
		if (!token || (!courseId && !wizardSessionId)) {
			return;
		}

		const socket = io(`${PARSED_ENV.VITE_API_URL}${MEDIA_JOBS_NAMESPACE}`, {
			auth: { token },
			query: { token },
			// The API runs several replicas/workers that don't share polling state, so the
			// multi-request polling handshake breaks. A websocket is one connection end to end.
			transports: ["websocket"],
			reconnection: true,
			reconnectionDelay: 1000,
			reconnectionDelayMax: 5000,
		});

		socketRef.current = socket;

		const join = () => {
			if (courseId) {
				socket.emit(MediaSocketEvent.JOIN_SCENARIO, { courseId });
			}

			if (wizardSessionId) {
				socket.emit(MediaSocketEvent.JOIN_WIZARD, { sessionId: wizardSessionId });
			}
		};

		socket.on("connect", join);
		socket.on(MediaSocketEvent.JOB_UPDATED, (event: MediaJobEvent) =>
			handlers.current.onJobUpdated?.(event)
		);
		socket.on(MediaSocketEvent.JOB_SUCCEEDED, (event: MediaJobEvent) =>
			handlers.current.onJobSucceeded?.(event)
		);
		socket.on(MediaSocketEvent.JOB_FAILED, (event: MediaJobEvent) =>
			handlers.current.onJobFailed?.(event)
		);
		socket.on(MediaSocketEvent.WIZARD_UPDATED, (aggregate: unknown) =>
			handlers.current.onWizardUpdated?.(aggregate)
		);

		return () => {
			if (courseId) {
				socket.emit(MediaSocketEvent.LEAVE_SCENARIO, { courseId });
			}

			if (wizardSessionId) {
				socket.emit(MediaSocketEvent.LEAVE_WIZARD, { sessionId: wizardSessionId });
			}

			socket.disconnect();
			socketRef.current = null;
		};
	}, [token, courseId, wizardSessionId]);

	return socketRef;
};
